<template>
  <div
    id="flashcard-view-container-v2"
    ref="flashcard-view-container"
    class="flashcard-view-container"
  >
    <breadcrumbs
      :breadcrumbs="breadcrumbs"
      version="v2"
    ></breadcrumbs>
    <template
      v-if="answered"
    >
      <answered-flashcard
        :answer-ids="answerIds"
        :category="currentFlashcardCategory"
        :correct="correct"
        :flashcard="currentFlashcard"
        :raw-answer-data="rawAnswerData"
      ></answered-flashcard>
      <answered-footer
        :category="currentFlashcardCategory"
        :flashcard="currentFlashcard"
        :show-flashcard-statuses="true"
        @tag="tryTagFlashcard"
      ></answered-footer>
    </template>

    <template
      v-else
    >
      <unanswered-header
        :category="currentFlashcardCategory"
        :flashcard="currentFlashcard"
        :is-learning-path="isLearningPath"
      ></unanswered-header>
      <unanswered-flashcard
        :answer-ids="answerIds"
        :category="currentFlashcardCategory"
        :flashcard="currentFlashcard"
        :raw-answer-data="rawAnswerData"
        :sharing-enabled="sharingEnabled"
        :terminology="isTerminology"
        @set-answer-ids="setAnswerIds"
        @set-raw-answer-data="setRawAnswerData"
        @try-answer-flashcard="tryAnswerFlashcard"
      ></unanswered-flashcard>
      <unanswered-footer
        :can-submit-answer="canSubmitAnswer"
        :category="currentFlashcardCategory"
        :flashcard="currentFlashcard"
        :is-learning-path="isLearningPath"
        @answer="answerFlashcard"
        @next-flashcard="goToNextFlashcard"
        @previous-flashcard="goToPreviousFlashcard"
      ></unanswered-footer>
    </template>
  </div>
</template>

<script>
import AnsweredFlashcard from '@/components/flashcards-v2/AnsweredFlashcard';
import AnsweredFooter from '@components/flashcards-v2/AnsweredFooter';
import Breadcrumbs from '@components/singletons/breadcrumbs';
import UnansweredFlashcard from '@components/flashcards-v2/UnansweredFlashcard';
import UnansweredFooter from '@components/flashcards-v2/UnansweredFooter';
import UnansweredHeader from '@/components/flashcards-v2/UnansweredHeader.vue';
import FlashcardViewMixin from '@/mixins/flashcardViews';
import GlobalMixin from '@/mixins/global';
import RateLimitMixin from '@/mixins/rate-limit';
import QuestionsUIV2Mixin from '@/mixins/questions-ui-v2';

export default {
  components: {
    AnsweredFlashcard,
    AnsweredFooter,
    Breadcrumbs,
    UnansweredFlashcard,
    UnansweredFooter,
    UnansweredHeader,
  },
  mixins: [
    FlashcardViewMixin,
    GlobalMixin,
    RateLimitMixin,
    QuestionsUIV2Mixin,
  ],
  // this is designed to capture users who have used the back button after starting the tour
  // so that we can reset the onTour variable and re-enable parts of the app we blur/disable during the tour
  beforeRouteLeave(to, from, next) {
    if (this.$store.getters['session/onTour']) {
      // the component at categories#complete will redirect to categories#index when a user is on the tour
      // so we need to make sure we don't reset the onTour variable when the user goes to that route
      if (to.name !== 'flashcard#show' && to.name !== 'categories#complete') {
        this.$store.commit('session/setOnTour', false);
        this.$store.commit('session/setTourType', '');
        this.$store.commit('navigationItems/resetDynamicNavigationItems');
      }
    }
    next();
  },
  data() {
    return {
      isLearningPath: true,
    };
  },
  computed: {
    breadcrumbs() {
      return [{ name: 'Learning Path' }];
    },
    canSubmitAnswer() {
      return this.hasBeenAnswered;
    },
    categoryId() {
      return this.$route.params.categoryId;
    },
    currentFlashcardCategory() {
      if (this.categoryId) {
        return this.$store.getters['categories/categories'][this.categoryId];
      }
      return {};
    },
    hasNoParams() {
      return !this.$route.params.categoryId && !this.$route.params.flashcardId;
    },
    fallbackCategoryId() {
      return this.$store.getters['learningPath/fallbackCategoryId'];
    },
    learningPath () {
      return this.$store.getters['learningPath/flashcards'];
    },
    nextCategoryId () {
      let index = this.learningPath.findIndex(f => f.flashcardId === this.currentFlashcard.id);
      return this.learningPath[index + 1]?.categoryId;
    },
    // Used in flashcardViews mixin
    nextFlashcardRoute() {
      if (!this.nextFlashcard) {
        return {
          name: 'learningPath#show',
        };
      }

      if (!this.nextFlashcard) {
        return { name: 'home' };
      }

      return {
        name: 'learningPath#show',
        params: {
          categoryId: this.nextCategoryId,
          flashcardId: this.nextFlashcard,
        },
        query: this.currentQuery,
      };
    },
    previousFlashcardRoute() {
      let index = this.learningPath.findIndex(f => f.flashcardId === this.currentFlashcard.id);
      return {
        name: 'learningPath#show',
        params: {
          categoryId: this.learningPath[index - 1]?.categoryId,
          flashcardId: this.learningPath[index - 1]?.flashcardId,
        },
        query: this.currentQuery,
      };
    },
    onTour() {
      return this.$store.getters['session/onTour'];
    },
    sharingEnabled() {
      const enabled = !!(this.$store.getters['productVars/get']['flashcardShareMessage']);
      return enabled && !this.onTour;
    },
  },
  watch: {
    '$route.params.flashcardId': function(id, oldId) {
      if (this.$store.getters['session/onTour']) {
        this.$analytics.events.viewedOnboardingFlashcard(id);
      }
      if(!this.$store.getters['flashcards/flashcards'][id]) {
        let params = { ids: [id], rateLimited: this.rateLimitingEnabled };
        this.$store.dispatch('flashcards/fetchFlashcards', params);
      }
      if (!id && oldId) {
        this.loadLearningPathFromApi();
      }
    },
    'currentFlashcard.id': {
      immediate: true,
      handler: function(id) {
        this.resetFlashcard(id);
        this.checkRateLimit();
      },
    },
  },
  mounted() {
    if (this.hasNoParams || !this.learningPath || this.learningPath.length === 0) {
      return this.loadLearningPathFromApi();
    } else if (!this.currentFlashcard.id) {
      this.loadLearningPathFlashcards();
    }
  },
  created() {
    this.$store.dispatch('flashcardStates/fetchStates', ['bookmarks', 'flashcards', 'stats']);
  },
  methods: {
    checkRateLimit() {
      this.$nextTick().then(() => {
        if (this.flashcardIsRateLimited(this.currentFlashcard) && this.creditLimitReached) {
          this.$modal.show('rate-limit-reached', {
            category: this.currentFlashcardCategory,
            flashcard: this.currentFlashcard,
          });
        }
      });
    },
    tryAnswerFlashcard() {
      if (this.canSubmitAnswer) {
        this.answerFlashcard();
      }
    },
    tryTagFlashcard(statusId) {
      return this.tagFlashcard(statusId);
    },
    loadLearningPathFlashcards() {
      this.$store.dispatch('flashcards/fetchFlashcards', {
        ids: this.learningPath.map(f => f.flashcardId),
        rateLimited: this.rateLimitingEnabled,
      });
    },
    loadLearningPathFromApi() {
      this.$store.dispatch('learningPath/fetchLearningPath')
        .then((data) => {
          // if there are no flashcards in the learning path, redirect to the fallback category
          if (data.length === 0 && this.fallbackCategoryId) {
            return this.$router.push({
              name: 'categories#index',
              params: {
                categoryId: this.fallbackCategoryId,
              },
            });
          // if there is no fallback category, redirect to the home page
          } else if (data.length === 0) {
            return this.$router.push({
              name: 'home',
            });
          }

          this.loadLearningPathFlashcards();
          this.$router.push({
            name: 'learningPath#show',
            params: {
              categoryId: this.learningPath[0].categoryId,
              flashcardId: this.learningPath[0].flashcardId,
            },
          });
        });
    },
  },
};
</script>

<style lang='scss' scoped>
  #flashcard-view-container-v2 {
    @include flashcard-view-container-v2;
  }
</style>
